import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);
const defaultDuration = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts(prev => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      className="fixed bottom-4 right-4 z-toast flex w-full max-w-sm flex-col gap-2 max-h-[calc(100vh-2rem)] overflow-y-auto pr-4 sm:pr-0"
      role="region"
      aria-live="polite"
      aria-label="Benachrichtigungen"
    >
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>,
    document.body
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const variant = toast.variant ?? 'default';
  const duration = toast.duration ?? defaultDuration;
  const [paused, setPaused] = useState(false);
  const remainingDuration = useRef(Math.max(0, duration));
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (duration <= 0 || paused) return undefined;

    startedAt.current = Date.now();
    const timer = window.setTimeout(() => onRemove(toast.id), remainingDuration.current);

    return () => {
      if (startedAt.current !== null) {
        remainingDuration.current = Math.max(0, remainingDuration.current - (Date.now() - startedAt.current));
      }
      window.clearTimeout(timer);
    };
  }, [duration, onRemove, paused, toast.id]);

  const variantClasses: Record<NonNullable<Toast['variant']>, string> = {
    default: 'border-neutral-200 dark:border-neutral-700',
    success: 'border-success-300 dark:border-success-800',
    error: 'border-emergency-300 dark:border-emergency-800',
    warning: 'border-warning-300 dark:border-warning-800',
    info: 'border-primary-300 dark:border-primary-800',
  };

  const iconMap: Record<NonNullable<Toast['variant']>, ComponentType<SVGProps<SVGSVGElement>>> = {
    default: icons.Info,
    success: icons.CheckCircle,
    error: icons.AlertCircle,
    warning: icons.AlertTriangle,
    info: icons.Info,
  };

  const iconClasses: Record<NonNullable<Toast['variant']>, string> = {
    default: 'text-primary-600 dark:text-primary-400',
    success: 'text-success-700 dark:text-success-400',
    error: 'text-emergency-700 dark:text-emergency-400',
    warning: 'text-warning-700 dark:text-warning-400',
    info: 'text-primary-600 dark:text-primary-400',
  };

  const Icon = iconMap[variant];

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border bg-white p-3.5 shadow-card dark:bg-neutral-900',
        variantClasses[variant]
      )}
      role={variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', iconClasses[variant])} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-5 text-neutral-900 dark:text-neutral-100">{toast.title}</p>
        {toast.description && (
          <p className="mt-1 text-sm leading-5 text-neutral-600 dark:text-neutral-300">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
        aria-label="Benachrichtigung schließen"
      >
        <icons.X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
